var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Head from 'next/head';
import { useCallback, useEffect, useRef, useState } from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { videoSource } from '../../common/data/videoSourceTypes.data';
import Icon from '../Icon/Icon';
import useStyles from './Video.styles';
import { useMediaQuery } from 'react-responsive';
import { useLoaded } from '../MainContent/useComponentLoading';
import { useTheme } from '@mui/material';
import clsx from 'clsx';
import usePortraitDetection from '../../hooks/usePortraitDetection';
const Video = (props) => {
    const { video, videoSchemaSEO, preRollSection, restoreOnLeave = true, isFromContentFeature, onPortraitDetected, } = props;
    const { autoPlay, loop, playButton, controls, playSound, url } = video;
    const { startTime, endTime } = preRollSection || {};
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const theme = useTheme();
    const [isVideoPlay, setIsPlayVideo] = useState(autoPlay !== null && autoPlay !== void 0 ? autoPlay : false);
    const [isIosMobile, setIsIosMobile] = useState(true);
    const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
    const [isFullPlayback, setIsFullPlayback] = useState(false);
    const [showPoster, setShowPoster] = useState(url && !isFromContentFeature && !autoPlay); // Show poster only if we have a URL, not from content feature, and not autoplaying
    const [isHovering, setIsHovering] = useState(false);
    const [isVideoLoaded, setIsVideoLoaded] = useState(true); // Prevent initial loading state
    const [isMounted, setIsMounted] = useState(false);
    const stableOnPortraitDetected = useCallback((portrait) => onPortraitDetected === null || onPortraitDetected === void 0 ? void 0 : onPortraitDetected(portrait), [onPortraitDetected]);
    const isPortrait = usePortraitDetection(videoRef, onPortraitDetected ? stableOnPortraitDetected : undefined);
    const { classes } = useStyles({ isFromContentFeature: isFromContentFeature !== null && isFromContentFeature !== void 0 ? isFromContentFeature : undefined, isPortrait });
    const isMobileDevice = !useMediaQuery({ query: `(min-width: ${theme.breakpoints.values.sm}px)` });
    const iosDevice = globalThis.window === undefined ? false : /iPhone/.test(globalThis.window.navigator.userAgent);
    const hasStartAndEndTimes = typeof startTime === 'number' && typeof endTime === 'number';
    const enableHover = hasStartAndEndTimes && startTime < endTime;
    const hasPrerollTimes = hasStartAndEndTimes;
    // Show play button when: poster is shown OR playButton prop is true OR has preroll times
    const shouldShowPlayButton = showPoster || playButton || hasPrerollTimes;
    useEffect(() => {
        if (shouldShowPlayButton && !isFromContentFeature) {
            setIsPlayVideo(false);
        }
    }, [shouldShowPlayButton, isFromContentFeature]);
    const pauseVideo = useCallback((video) => {
        video.pause();
        setIsPlayVideo(false);
    }, []);
    const playVideo = useCallback((video) => {
        if (!isFullPlayback) {
            video.currentTime = 0;
            setIsFullPlayback(true);
        }
        if (playSound) {
            video.muted = false;
        }
        video.play();
        setIsPlayVideo(true);
        setShowPoster(false);
    }, [isFullPlayback, playSound]);
    const handleVideoPlay = () => {
        const video = videoRef.current;
        if (!video)
            return;
        if (isVideoPlay) {
            pauseVideo(video);
        }
        else {
            playVideo(video);
        }
    };
    // Extracted preroll logic to reduce cognitive complexity
    const startPreroll = useCallback((videoElement) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            videoElement.currentTime = startTime;
            videoElement.loop = false;
            videoElement.muted = true;
            yield videoElement.play();
        }
        catch (error) {
            console.warn('Video preroll failed:', error);
        }
    }), [startTime]);
    const stopPrerollAtEnd = useCallback((videoElement, currentTime) => {
        if (currentTime >= endTime) {
            setIsHovering(false);
            videoElement.pause();
            videoElement.currentTime = endTime;
        }
    }, [endTime]);
    // Video hover functionality
    useEffect(() => {
        const videoElement = videoRef.current;
        const containerElement = containerRef.current;
        if (!containerElement || !videoElement || !enableHover) {
            return;
        }
        let originalTime = 0;
        const handleMouseEnter = () => __awaiter(void 0, void 0, void 0, function* () {
            if (isHovering)
                return;
            setIsHovering(true);
            setShowPoster(false);
            if (!isFullPlayback) {
                originalTime = videoElement.currentTime;
                yield startPreroll(videoElement);
            }
        });
        const handleMouseLeave = () => {
            const canShowPoster = !isFullPlayback && !!url && !isFromContentFeature;
            if (canShowPoster) {
                setShowPoster(true);
            }
            if (!isHovering)
                return;
            setIsHovering(false);
            if (!isFullPlayback) {
                videoElement.pause();
                if (restoreOnLeave) {
                    videoElement.currentTime = originalTime;
                }
            }
        };
        const handleTimeUpdate = () => {
            if (isHovering && !isFullPlayback) {
                stopPrerollAtEnd(videoElement, videoElement.currentTime);
            }
        };
        containerElement.addEventListener('mouseenter', handleMouseEnter);
        containerElement.addEventListener('mouseleave', handleMouseLeave);
        videoElement.addEventListener('timeupdate', handleTimeUpdate);
        return () => {
            containerElement.removeEventListener('mouseenter', handleMouseEnter);
            containerElement.removeEventListener('mouseleave', handleMouseLeave);
            videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        };
    }, [
        startTime,
        endTime,
        restoreOnLeave,
        isFullPlayback,
        enableHover,
        isHovering,
        url,
        isFromContentFeature,
        startPreroll,
        stopPrerollAtEnd,
    ]);
    useEffect(() => {
        if (globalThis.window === undefined)
            return;
        const handleIsGalleryModalChange = (event) => {
            if (event.type === 'isGalleryModalOpen') {
                setIsGalleryModalOpen(sessionStorage.getItem('isGalleryModalOpen') === 'true');
            }
        };
        globalThis.window.addEventListener('isGalleryModalOpen', handleIsGalleryModalChange);
        return () => {
            globalThis.window.removeEventListener('isGalleryModalOpen', handleIsGalleryModalChange);
        };
    }, []);
    // Handle clicks anywhere on the video container
    const handleContainerClick = (event) => {
        event.preventDefault();
        const video = videoRef.current;
        if (!video)
            return;
        if (isVideoPlay) {
            pauseVideo(video);
        }
        else {
            playVideo(video);
        }
    };
    // Reset full playback mode when video ends and handle video loading
    useEffect(() => {
        const video = videoRef.current;
        if (!video)
            return;
        const handleVideoEnd = () => {
            setIsFullPlayback(false);
            // Show poster when video ends (if we have URL and not from content feature)
            if (url && !isFromContentFeature) {
                setShowPoster(true);
            }
        };
        const handleVideoLoadedData = () => {
            setIsVideoLoaded(true);
        };
        const handleVideoLoadedMetadata = () => {
            setIsVideoLoaded(true);
        };
        video.addEventListener('ended', handleVideoEnd);
        video.addEventListener('loadeddata', handleVideoLoadedData);
        video.addEventListener('loadedmetadata', handleVideoLoadedMetadata);
        // Fallback: if metadata is already loaded (cached), mark as loaded immediately
        if (video.readyState >= 1) {
            setIsVideoLoaded(true);
        }
        return () => {
            video.removeEventListener('ended', handleVideoEnd);
            video.removeEventListener('loadeddata', handleVideoLoadedData);
            video.removeEventListener('loadedmetadata', handleVideoLoadedMetadata);
        };
    }, [url, isFromContentFeature]);
    useEffect(() => {
        if (isGalleryModalOpen && autoPlay && !!(videoRef === null || videoRef === void 0 ? void 0 : videoRef.current)) {
            videoRef.current.pause();
            setIsPlayVideo(false);
        }
    }, [autoPlay, isGalleryModalOpen]);
    useEffect(() => {
        setIsIosMobile(isMobileDevice && iosDevice);
    }, [isMobileDevice, iosDevice]);
    // Set mounted after ensuring parent containers are established
    useEffect(() => {
        const setMountedOnce = () => {
            setIsMounted(true);
        };
        // Use requestAnimationFrame to ensure DOM is ready but mount quickly
        const frameId = requestAnimationFrame(setMountedOnce);
        // Fallback timeout to ensure component always mounts
        const fallbackTimer = setTimeout(setMountedOnce, 200);
        return () => {
            cancelAnimationFrame(frameId);
            clearTimeout(fallbackTimer);
        };
    }, []);
    useLoaded(props);
    return (_jsxs("div", { style: {
            position: 'relative',
            width: '100%',
            height: isFromContentFeature ? '100%' : 'auto',
            minHeight: isFromContentFeature ? 'unset' : '200px',
            aspectRatio: isFromContentFeature ? 'unset' : '16/9',
            overflow: 'hidden',
            contain: isPortrait ? 'layout style' : 'layout size style',
            visibility: isMounted ? 'visible' : 'hidden',
            opacity: isMounted ? 1 : 0,
            transform: 'translateZ(0)', // Hardware acceleration
        }, children: [_jsxs("button", { type: "button", className: clsx(classes.container, classes.videoContainerSection), ref: containerRef, "data-testid": "video-container", onClick: handleContainerClick, "aria-label": `${isVideoPlay ? 'Pause' : 'Play'} video`, children: [url && (_jsx("div", { className: classes.portraitBlurredBg, "aria-hidden": "true", children: _jsx("img", { src: `${url}?fmt=jpg`, alt: "" }) })), isMounted && !isVideoLoaded && !showPoster && url && (_jsx("div", { className: classes.loadingPlaceholder, "aria-label": "Video loading" })), showPoster && url && !isFromContentFeature && (_jsx("img", { src: `${url}?fmt=jpg`, alt: "Video thumbnail", className: clsx(classes.posterThumbnail, classes.posterThumbnailInlineSytles) })), enableHover && (_jsx("button", { type: "button", className: classes.containerHoverOverlay, "aria-label": "Video hover preview", onClick: (event) => {
                            handleContainerClick(event);
                        } })), _jsx("video", { id: "video", controls: controls !== null && controls !== void 0 ? controls : false, muted: isFromContentFeature && (autoPlay !== null && autoPlay !== void 0 ? autoPlay : false) ? true : !playSound, autoPlay: enableHover ? false : autoPlay !== null && autoPlay !== void 0 ? autoPlay : false, loop: loop !== null && loop !== void 0 ? loop : false, ref: videoRef, preload: "metadata", disablePictureInPicture: true, controlsList: 'nodownload noplaybackrate', playsInline: true, poster: url && !isFromContentFeature ? `${url}?fmt=jpg` : undefined, className: clsx(classes.videoContainerElement, classes.videoContainerInlineStyles), style: isPortrait ? { objectFit: 'contain', background: 'transparent' } : undefined, children: videoSource.map((video) => (_jsx("source", { "data-quality-label": video.quality, "data-bitrate": video.birtrate, src: isIosMobile ? `${url}${video.urlExtension}#t=0` : `${url}${video.urlExtension}`, type: video.videoType, media: `screen and (${video.deviceWidth}:801px)` }, `${url}${video.urlExtension}`))) }), isMounted && !isVideoPlay && shouldShowPlayButton && (_jsx("div", { className: classes.controlsContainer, children: _jsx(Icon, { icon: "play_circle_outline", className: classes.icon, onClick: handleVideoPlay }) }))] }, url), videoSchemaSEO ? (_jsx(Head, { children: _jsx("script", Object.assign({}, jsonLdScriptProps(JSON.parse(videoSchemaSEO)))) })) : null] }));
};
export default Video;
